/**
 * "Limits & defaults" — Reference section (ADR-0074).
 * A single lookup for cross-cutting platform facts: data region, operational log
 * retention, the rate-limit/payload responses, the metering unit, and the
 * supported locales. Each row links to the page that owns the detail; numbers
 * that vary by plan point to /pricing rather than being restated.
 */
import type { DocPageContent } from '../types';

export const REFERENCE_LIMITS_PAGE: DocPageContent = {
  slug: 'reference/limits',
  titleKey: 'docs.reference.limits.title',
  descriptionKey: 'docs.reference.limits.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.reference.limits.lead' },

    {
      kind: 'heading',
      level: 2,
      id: 'platform',
      textKey: 'docs.reference.limits.platform.heading',
    },
    {
      kind: 'definitions',
      items: [
        {
          termKey: 'docs.reference.limits.region.term',
          descKey: 'docs.reference.limits.region.desc',
        },
        { termKey: 'docs.reference.limits.logs.term', descKey: 'docs.reference.limits.logs.desc' },
        { termKey: 'docs.reference.limits.rate.term', descKey: 'docs.reference.limits.rate.desc' },
        {
          termKey: 'docs.reference.limits.payload.term',
          descKey: 'docs.reference.limits.payload.desc',
        },
      ],
    },

    { kind: 'heading', level: 2, id: 'usage', textKey: 'docs.reference.limits.usage.heading' },
    {
      kind: 'definitions',
      items: [
        {
          termKey: 'docs.reference.limits.usageCredits.term',
          descKey: 'docs.reference.limits.usageCredits.desc',
        },
        {
          termKey: 'docs.reference.limits.approaching.term',
          descKey: 'docs.reference.limits.approaching.desc',
        },
        {
          termKey: 'docs.reference.limits.spend.term',
          descKey: 'docs.reference.limits.spend.desc',
        },
      ],
    },
    {
      kind: 'callout',
      tone: 'note',
      titleKey: 'docs.reference.limits.plan.title',
      textKey: 'docs.reference.limits.plan.body',
      links: {
        pricing: { target: { path: '/pricing' }, labelKey: 'docs.common.link.pricing' },
      },
    },

    { kind: 'heading', level: 2, id: 'locales', textKey: 'docs.reference.limits.locales.heading' },
    { kind: 'paragraph', textKey: 'docs.reference.limits.locales.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.reference.limits.locale.en',
        'docs.reference.limits.locale.es',
        'docs.reference.limits.locale.fr',
        'docs.reference.limits.locale.de',
        'docs.reference.limits.locale.ar',
        'docs.reference.limits.locale.he',
      ],
    },

    { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.reference.limits.next.usage.title',
          textKey: 'docs.reference.limits.next.usage.text',
          to: { docSlug: 'billing/usage' },
        },
        {
          titleKey: 'docs.reference.limits.next.limits.title',
          textKey: 'docs.reference.limits.next.limits.text',
          to: { docSlug: 'billing/limits' },
        },
        {
          titleKey: 'docs.reference.limits.next.languages.title',
          textKey: 'docs.reference.limits.next.languages.text',
          to: { docSlug: 'languages' },
        },
      ],
    },
  ],
};
